import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast';
import { useAddLink } from '@/hooks/use-links';
import { describeError } from '@/lib/api-client';
import { firstSharerName, formatDate } from '@/lib/format';
import type { AddLinkResult } from '@/types/api';

/**
 * People paste addresses the way their browser shows them, often without the scheme. The API only
 * takes absolute http(s) URLs, so a bare `example.com/page` is completed here, and anything with a
 * scheme of its own is sent as typed for the API to accept or explain.
 */
export function withScheme(input: string): string {
  const trimmed = input.trim();
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/**
 * Adding a link by hand: what sharing it in Slack does, for whoever is signed in. A new link opens
 * on its own page, which follows enrichment as it runs. One that is already in the catalogue is
 * shown with its first sharer — first link wins, so nothing changes.
 */
export function AddLinkDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const addLink = useAddLink();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [url, setUrl] = useState('');
  const [existing, setExisting] = useState<AddLinkResult | null>(null);

  const { reset } = addLink;
  useEffect(() => {
    if (!open) {
      setUrl('');
      setExisting(null);
      reset();
    }
  }, [open, reset]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!url.trim() || addLink.isPending) return;
    setExisting(null);

    addLink.mutate(
      { url: withScheme(url) },
      {
        onSuccess: (result) => {
          if (result.outcome === 'existing') {
            setExisting(result);
            return;
          }
          onOpenChange(false);
          toast({
            title: 'Link added',
            description: 'SLC is reading it now: title, summary, categories and a preview.',
            variant: 'success',
          });
          if (result.link) void navigate(`/links/${result.link.linkId}`);
        },
      },
    );
  };

  const owner = existing?.link;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a link</DialogTitle>
          <DialogDescription>
            It joins the catalogue credited to you, and is read, summarised and categorised like a
            link shared in Slack.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="add-link-url">Link</Label>
            <Input
              id="add-link-url"
              inputMode="url"
              autoComplete="url"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="https://example.com/article"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                setExisting(null);
                if (addLink.isError) addLink.reset();
              }}
              aria-invalid={addLink.isError || undefined}
              aria-describedby={addLink.isError ? 'add-link-error' : undefined}
            />
          </div>

          {addLink.isError ? (
            <p
              id="add-link-error"
              role="alert"
              className="flex items-start gap-2 text-sm text-destructive"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
              {describeError(addLink.error)}
            </p>
          ) : null}

          {existing ? (
            <div
              role="status"
              className="flex items-start gap-3 rounded-md border border-border bg-muted/50 p-3 text-sm"
            >
              <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <div className="flex min-w-0 flex-col gap-1">
                <p className="font-medium">Already in the catalogue</p>
                <p className="text-muted-foreground">
                  {owner
                    ? `First shared by ${firstSharerName(owner.firstShare)} on ${formatDate(owner.firstShare.sharedAt)}.`
                    : 'Someone shared it a moment ago; it will show up in the catalogue shortly.'}
                </p>
                {owner ? (
                  <Link
                    to={`/links/${owner.linkId}`}
                    onClick={() => onOpenChange(false)}
                    className="inline-flex w-fit items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                  >
                    Open it
                    <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!url.trim()} loading={addLink.isPending}>
              Add link
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
